import { RumErrorEvent, RumErrorEventDomainContext, RumResourceEvent, datadogRum } from '@datadog/browser-rum';
import { isBotUserAgent, isE2ESession, isFeatureEnabled } from 'common/utils';

import { getDatadogProxy } from './datadogProxy';
import {
  containsReportDataSignedUrl,
  sanitizeErrorForRum,
  sanitizeRumErrorText,
  stripReportDataSignaturesDeep,
  stripReportDataSignaturesInText,
} from './reportDataSignedUrl';

/**
 * CefSharp email-scanner errors. Email security scanners (e.g. Outlook Safe Links) pre-check URLs with an
 * embedded browser that has known bugs. See: https://github.com/DataDog/browser-sdk/issues/2715
 */
const CEF_SHARP_ERROR_PATTERN = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

export const initDatadogRum = () => {
  // Don't initialize Datadog in E2E test sessions, if bot detected, or if feature is disabled
  if (isE2ESession() || isBotUserAgent() || !isFeatureEnabled('DatadogEnabled')) {
    return;
  }

  datadogRum.init({
    applicationId: '0e7b150d-d0c4-4736-a9c2-3eb1cfd0b8ff',
    clientToken: window._env_.DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'natter-frontend',
    env: window._env_.VITE_APP_ENVIRONMENT,
    version: window._env_.VITE_APP_TAG,
    // Same-origin intake proxy (telemetry-relay via CloudFront /ddintake/*), gated by DatadogProxyEnabled.
    // undefined when the flag is off → SDK posts directly to Datadog.
    proxy: getDatadogProxy(),
    sessionSampleRate: window._env_.DATADOG_SESSION_SAMPLE_RATE
      ? Number(window._env_.DATADOG_SESSION_SAMPLE_RATE)
      : 100,
    sessionReplaySampleRate: window._env_.DATADOG_SESSION_REPLAY_SAMPLE_RATE
      ? Number(window._env_.DATADOG_SESSION_REPLAY_SAMPLE_RATE)
      : 100,
    defaultPrivacyLevel: 'mask-user-input',
    // Link RUM resources to backend APM traces; matches the api./dev-api./permissions-api. host on every env (excludes auth-api, natalie, third parties).
    allowedTracingUrls: [
      {
        // Anchored both ends: SDK matches via unanchored RegExp.test, so without ^ + host-boundary look-alike hosts would leak trace headers.
        match: /^https:\/\/(api|dev-api|permissions-api)\.([a-z0-9-]+\.)*natter\.(co|network)(?:[/?#:]|$)/,
        // This list is coupled to each matched host's CORS config, and `permissions-api` is browser-facing
        // so its coupling is load-bearing. These two propagators inject six headers - `traceparent`,
        // `tracestate` and four `x-datadog-*` - and a preflight naming one the gateway does not allow is
        // answered with no `access-control-*` headers at all. That blocks the request before it is sent,
        // which is indistinguishable from the service being down: every permission check then denies.
        //
        // So adding a propagator, or a host whose CORS you do not control, needs the allow-list extended
        // first - `CORS_PREFLIGHT.allowHeaders` in permissions-service, and its ADR-0010 for why an
        // omission is a total outage rather than a lost trace.
        propagatorTypes: ['tracecontext', 'datadog'],
      },
    ],
    traceSampleRate: window._env_.DATADOG_TRACE_SAMPLE_RATE ? Number(window._env_.DATADOG_TRACE_SAMPLE_RATE) : 100,
    // A signed report payload link reads the payload, quotes included, while it is valid (CORE-569), so
    // every field that may carry one loses its signature, and the event is still sent.
    beforeSend: (event, domainContext) => {
      if (event.type === 'error') {
        const { error } = event as RumErrorEvent;

        if (CEF_SHARP_ERROR_PATTERN.test(error.message)) return false;

        // RUM discards edits to `causes`. The SDK only lists causes for an Error, and that Error is the
        // domain context's, so report a copy of it without the link instead. The copy quotes no signed
        // link, so it passes through here rather than being replaced again. Reporting from inside
        // `beforeSend` is safe: the SDK re-enters this pipeline the same way to report its own errors.
        const causeQuotesSignedUrl = error.causes?.some(
          ({ message, stack }) => containsReportDataSignedUrl(message) || containsReportDataSignedUrl(stack)
        );
        if (causeQuotesSignedUrl) {
          const { error: original } = domainContext as RumErrorEventDomainContext;
          if (original instanceof Error) {
            datadogRum.addError(sanitizeErrorForRum(original), stripReportDataSignaturesDeep(event.context));
          }

          return false;
        }

        error.message = sanitizeRumErrorText(error.message);
        if (error.stack) error.stack = sanitizeRumErrorText(error.stack);
        if (error.resource) error.resource.url = stripReportDataSignaturesInText(error.resource.url);
      }

      if (event.type === 'resource') {
        const { resource } = event as RumResourceEvent;
        resource.url = stripReportDataSignaturesInText(resource.url);
      }

      event.view.url = stripReportDataSignaturesInText(event.view.url);
      if (event.view.referrer) event.view.referrer = stripReportDataSignaturesInText(event.view.referrer);
      if (event.context) event.context = stripReportDataSignaturesDeep(event.context);

      return true;
    },
  });
};
